import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, Award, Dumbbell, Shield, Star, MapPin, Clock, Filter, Loader2, ChevronRight } from "lucide-react";

const ROLE_ICONS = { coach: Award, trainer: Dumbbell, official: Shield };
const ROLE_COLORS = { coach: "bg-blue-100 text-blue-700", trainer: "bg-red-100 text-red-700", official: "bg-purple-100 text-purple-700" };
const STATUS_COLORS = { verified: "bg-green-100 text-green-800", pending: "bg-amber-100 text-amber-800" };

export default function SPSearch() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sport, setSport] = useState("");
  const [role, setRole] = useState("");
  const [city, setCity] = useState("");
  const [sortBy, setSortBy] = useState("rating");

  useEffect(() => { search(); }, []);

  const search = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.set("q", query);
      if (sport) params.set("sport", sport);
      if (role) params.set("role", role);
      if (city) params.set("city", city);
      params.set("sort_by", sortBy);
      params.set("verified_only", "true");
      const res = await api.get(`/sp/search?${params}`);
      setResults(res.data.professionals || []);
    } catch { toast.error("Search failed"); }
    setLoading(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="sp-search">
      <h1 className="font-heading text-2xl sm:text-3xl">Find a Sports Professional</h1>

      {/* Search Filters */}
      <div className="flex gap-3 flex-wrap items-end">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search by name or sport..." className="pl-9" data-testid="sp-search-input"
            onKeyDown={e => e.key === "Enter" && search()} />
        </div>
        <Select value={role} onValueChange={v => setRole(v === "all" ? "" : v)}>
          <SelectTrigger className="w-32"><SelectValue placeholder="Role" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="coach">Coach</SelectItem>
            <SelectItem value="trainer">Trainer</SelectItem>
            <SelectItem value="official">Official</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="rating">Top Rated</SelectItem>
            <SelectItem value="sessions">Most Booked</SelectItem>
            <SelectItem value="price">Lowest Price</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={search} className="rounded-full"><Search className="w-4 h-4 mr-1" />Search</Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : results.length === 0 ? (
        <div className="text-center py-16">
          <Search className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-heading text-lg text-muted-foreground">No professionals found</p>
          <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">{results.length} professional{results.length !== 1 ? "s" : ""} found</p>
          {results.map(sp => (
            <Card key={sp.sp_id} className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => navigate(`/sp/view/${sp.sp_id}`)} data-testid={`sp-card-${sp.sp_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-heading text-base font-bold">{sp.name}</h3>
                      {sp.roles_enabled?.map(r => {
                        const I = ROLE_ICONS[r] || Award;
                        return <Badge key={r} className={`${ROLE_COLORS[r]} text-[9px]`}><I className="w-3 h-3 mr-0.5" />{r}</Badge>;
                      })}
                      {sp.verification_status === "verified" && <Badge className="bg-green-100 text-green-800 text-[9px]">Verified</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">{sp.primary_sport} · {sp.years_experience} yrs exp</p>
                    {sp.bio && <p className="text-xs text-muted-foreground line-clamp-1">{sp.bio}</p>}
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                      {sp.service_locations?.length > 0 && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3" />{sp.service_locations.slice(0,2).join(", ")}</span>}
                      <span className="flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-500" />{sp.rating || 0} ({sp.total_ratings})</span>
                      <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{sp.total_sessions} sessions</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-4">
                    {sp.pricing?.per_hour && <p className="font-heading font-bold text-primary">₱{sp.pricing.per_hour}<span className="text-xs text-muted-foreground">/hr</span></p>}
                    <ChevronRight className="w-4 h-4 text-muted-foreground mt-1 ml-auto" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
